import React, { useEffect, useState } from 'react';
import { HitApi } from '../../../Api/ApiHit';
import { searchBill } from '../../../constant/Constant';
import { useDispatch, useSelector } from 'react-redux';
import { setApiJson } from '../../../redux/Actions/ApiAction';
import { setPagination } from '../../../redux/Actions/TableAction';
import PageHeader from '../../../PageHeader/PageHeader';
import AppTable from '../../../components/AppTable/AppTable';
import DateSearch from '../../../components/DateSearch/DateSearch';
import toast from 'react-hot-toast';
import { exportToCSV } from '../../../utils/exportUtils';

function BillReportMaster() {
    const dispatch = useDispatch();
    const ApiReducer = useSelector((state) => state.ApiReducer);
    const TableDataReducer = useSelector((state) => state.TableReducer);

    const [isSearching, setIsSearching] = useState(false);
    const [activeDateRange, setActiveDateRange] = useState(null);
    const [processedData, setProcessedData] = useState([]);

    // GST Rates
    const GST_RATE = 18;
    const SERVICE_GST_RATE = GST_RATE;
    const PRODUCT_GST_RATE = GST_RATE;

    useEffect(() => {
        if (activeDateRange) {
            performDateRangeSearch(activeDateRange.startDate, activeDateRange.endDate);
        }
    }, [TableDataReducer.pagination.page, TableDataReducer.pagination.limit]);

    // Process bills data - separate Services and Products into different rows
    const processBillsData = (bills) => {
        const rows = [];

        // Bill Reports should include only paid bills and exclude cancelled bills.
        const excludedStatuses = ['cancelled'];
        const filteredBills = bills.filter((bill) =>
            !excludedStatuses.includes(bill.status?.toLowerCase()) &&
            !excludedStatuses.includes(bill.billStatus?.toLowerCase()) &&
            bill.payment?.paymentStatus === 'Paid'
        );

        filteredBills.forEach((bill) => {
            const customerName = bill.client?.name || 'N/A';
            const invoiceDate = formatDate(bill.timestamp);
            const invoiceNo = bill.billNumber || 'N/A';
            const changeReturned = bill.changeReturned || 0;
            const paymentMethod = getPaymentMethods(bill.payment);
            const paymentBreakdown = getPaymentBreakdown(bill.payment, changeReturned);
            const totalPaid = (bill.payment?.totalPaid || 0) - changeReturned;
            const remarks = bill.remarks || '';
            const roundOffAmount = bill.calculations?.totals?.roundOffAmount || 0;

            // Track if roundoff has been applied
            let roundOffApplied = false;

            // Calculate totals for balance
            let servicesTotal = 0;
            let productsTotal = 0;

            // Process Services
            if (bill.services && bill.services.length > 0) {
                let servicesTaxableValue = 0;

                bill.services.forEach((service) => {
                    const qty = service.quantity || 1;
                    const price = (service.pricing?.finalPrice || service.pricing?.totalPrice || 0) * qty;
                    servicesTaxableValue += price;
                });

                // Add roundoff to first category
                if (!roundOffApplied && roundOffAmount !== 0) {
                    servicesTaxableValue += roundOffAmount;
                    roundOffApplied = true;
                }

                // Calculate GST for services (18%)
                const taxableValue = servicesTaxableValue / (1 + SERVICE_GST_RATE / 100);
                const gstAmount = servicesTaxableValue - taxableValue;
                servicesTotal = servicesTaxableValue;

                rows.push({
                    customerName,
                    invoiceDate,
                    invoiceNo,
                    invoiceType: 'Services',
                    remarks,
                    taxableValue: taxableValue.toFixed(2),
                    gst: gstAmount.toFixed(2),
                    invoiceAmount: servicesTaxableValue.toFixed(2),
                    paymentMethod,
                    paymentBreakdown,
                    totalPaid: '',
                    _billId: bill._id,
                    _isFirst: true
                });
            }

            // Process Products
            if (bill.products && bill.products.length > 0) {
                let productsTaxableValue = 0;

                bill.products.forEach((product) => {
                    const qty = product.quantity || 1;
                    const price = (product.pricing?.finalPrice || product.pricing?.totalPrice || 0) * qty;
                    productsTaxableValue += price;
                });

                // Add roundoff to products if not already applied to services
                if (!roundOffApplied && roundOffAmount !== 0) {
                    productsTaxableValue += roundOffAmount;
                    roundOffApplied = true;
                }

                // Calculate GST for products (18%)
                const taxableValue = productsTaxableValue / (1 + PRODUCT_GST_RATE / 100);
                const gstAmount = productsTaxableValue - taxableValue;
                productsTotal = productsTaxableValue;

                const isFirst = !bill.services || bill.services.length === 0;

                rows.push({
                    customerName: isFirst ? customerName : '',
                    invoiceDate: isFirst ? invoiceDate : '',
                    invoiceNo,
                    invoiceType: 'Products',
                    remarks: isFirst ? remarks : '',
                    taxableValue: taxableValue.toFixed(2),
                    gst: gstAmount.toFixed(2),
                    invoiceAmount: productsTaxableValue.toFixed(2),
                    paymentMethod: isFirst ? paymentMethod : '',
                    paymentBreakdown: isFirst ? paymentBreakdown : null,
                    totalPaid: '',
                    _billId: bill._id,
                    _isFirst: isFirst
                });
            }

            // Process Memberships
            if (bill.newMemberships && bill.newMemberships.length > 0) {
                let membershipsTaxableValue = 0;

                bill.newMemberships.forEach((membership) => {
                    const price = membership.pricing?.finalPrice || membership.pricing?.purchaseAmount || 0;
                    membershipsTaxableValue += price;
                });

                // Add roundoff to memberships if not already applied
                if (!roundOffApplied && roundOffAmount !== 0) {
                    membershipsTaxableValue += roundOffAmount;
                    roundOffApplied = true;
                }

                const isFirst = (!bill.services || bill.services.length === 0) && (!bill.products || bill.products.length === 0);

                rows.push({
                    customerName: isFirst ? customerName : '',
                    invoiceDate: isFirst ? invoiceDate : '',
                    invoiceNo,
                    invoiceType: 'Memberships',
                    remarks: isFirst ? remarks : '',
                    taxableValue: '0.00',
                    gst: '0.00',
                    invoiceAmount: membershipsTaxableValue.toFixed(2),
                    paymentMethod: isFirst ? paymentMethod : '',
                    paymentBreakdown: isFirst ? paymentBreakdown : null,
                    totalPaid: '',
                    _billId: bill._id,
                    _isFirst: isFirst
                });
            }

            // Add Total Paid to first row of this
            const firstRowIndex = rows.findIndex(r => r._billId === bill._id && r._isFirst);
            if (firstRowIndex !== -1) {
                rows[firstRowIndex].totalPaid = totalPaid.toFixed(2);
            }
        });

        return rows;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getPaymentMethods = (payment) => {
        if (!payment?.activePaymentMethods || payment.activePaymentMethods.length === 0) {
            return 'N/A';
        }
        return payment.activePaymentMethods.map(m => m.method).join(', ');
    };

    // Get payment breakdown by method
    const getPaymentBreakdown = (payment, changeReturned = 0) => {
        const breakdown = {
            cash: 0,
            upi: 0,
            wallet: 0,
            card: 0,
            other: 0
        };

        if (!payment?.activePaymentMethods || payment.activePaymentMethods.length === 0) {
            return breakdown;
        }

        payment.activePaymentMethods.forEach((method) => {
            const methodName = method.method?.toLowerCase() || '';
            const amount = parseFloat(method.amount) || 0;

            if (methodName.includes('cash')) {
                breakdown.cash += amount;
            } else if (methodName.includes('upi')) {
                breakdown.upi += amount;
            } else if (methodName.includes('wallet')) {
                breakdown.wallet += amount;
            } else if (methodName.includes('card') || methodName.includes('credit') || methodName.includes('debit')) {
                breakdown.card += amount;
            } else {
                breakdown.other += amount;
            }
        });

        // Subtract change returned from cash
        if (changeReturned > 0) {
            breakdown.cash = Math.max(0, breakdown.cash - changeReturned);
        }

        return breakdown;
    };

    const performDateRangeSearch = (startDate, endDate) => {
        setIsSearching(true);

        const startTimestamp = new Date(startDate).setHours(0, 0, 0, 0);
        const endTimestamp = new Date(endDate).setHours(23, 59, 59, 999);

        const searchObj = {
            createdAt: {
                $gte: startTimestamp,
                $lte: endTimestamp
            },
            status: { $nin: ['pending', 'cancelled'] },
            billStatus: { $nin: ['PENDING', 'CANCELLED'] }
        };

        const json = {
            page: TableDataReducer.pagination.page,
            limit: TableDataReducer.pagination.limit,
            search: searchObj
        };

        HitApi(json, searchBill).then((res) => {
            if (res?.statusCode === 200) {
                const billData = Array.isArray(res?.data?.docs) ? res?.data?.docs : [];
                dispatch(setApiJson(billData));

                // Process data for table display
                const processed = processBillsData(billData);
                setProcessedData(processed);

                const paginationData = {
                    total: res?.data?.totalDocs || 0,
                    page: TableDataReducer.pagination.page,
                    limit: TableDataReducer.pagination.limit
                };

                dispatch(setPagination(paginationData));
                toast.success(`Found ${res?.data?.totalDocs || 0} bills`);
            } else {
                dispatch(setApiJson([]));
                setProcessedData([]);
                toast.error('Failed to fetch bills');
            }
        }).catch(() => {
            dispatch(setApiJson([]));
            setProcessedData([]);
            toast.error('Error fetching bills');
        }).finally(() => {
            setIsSearching(false);
        });
    };

    const handleDateRangeSearch = (startDate, endDate) => {
        setActiveDateRange({ startDate, endDate });
        performDateRangeSearch(startDate, endDate);
    };

    const handleClearSearch = () => {
        setActiveDateRange(null);
        dispatch(setApiJson([]));
        setProcessedData([]);
    };

    const tableHeaders = [
        {
            title: "Customer Info",
            key: "customerName",
            render: (value, row) => (
                <div>
                    <div className="font-medium text-gray-900">{value}</div>
                    {row.invoiceDate && (
                        <div className="text-xs text-gray-500">{row.invoiceDate}</div>
                    )}
                </div>
            )
        },
        {
            title: "Invoice",
            key: "invoiceNo",
            render: (value, row) => {
                const bgColor = row.invoiceType === 'Services' ? 'bg-blue-100 text-blue-800' : row.invoiceType === 'Memberships' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800';
                return (
                    <div>
                        <div className="font-medium text-blue-600">{value}</div>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${bgColor}`}>
                            {row.invoiceType}
                        </span>
                    </div>
                );
            }
        },
        {
            title: "Amount Details",
            key: "taxableValue",
            render: (value, row) => {
                const gstRate = `${GST_RATE}%`;
                return (
                    <div className="text-xs space-y-0.5">
                        <div className="flex justify-between">
                            <span className="text-gray-500">Taxable:</span>
                            <span className="font-medium">₹{value}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500">GST ({gstRate}):</span>
                            <span className="font-medium">₹{row.gst}</span>
                        </div>
                        <div className="flex justify-between border-t pt-0.5">
                            <span className="text-gray-700 font-medium">Total:</span>
                            <span className="font-bold">₹{row.invoiceAmount}</span>
                        </div>
                    </div>
                );
            }
        },
        {
            title: "Payment Breakdown",
            key: "paymentBreakdown",
            render: (value, row) => {
                if (!value) return null;
                return (
                    <div className="text-xs space-y-0.5">
                        {value.cash > 0 && (
                            <div className="flex justify-between">
                                <span className="text-gray-500">Cash:</span>
                                <span className="font-medium">₹{value.cash.toFixed(2)}</span>
                            </div>
                        )}
                        {value.upi > 0 && (
                            <div className="flex justify-between">
                                <span className="text-gray-500">UPI:</span>
                                <span className="font-medium">₹{value.upi.toFixed(2)}</span>
                            </div>
                        )}
                        {value.wallet > 0 && (
                            <div className="flex justify-between">
                                <span className="text-gray-500">Wallet:</span>
                                <span className="font-medium">₹{value.wallet.toFixed(2)}</span>
                            </div>
                        )}
                        {value.card > 0 && (
                            <div className="flex justify-between">
                                <span className="text-gray-500">Card:</span>
                                <span className="font-medium">₹{value.card.toFixed(2)}</span>
                            </div>
                        )}
                        {value.other > 0 && (
                            <div className="flex justify-between">
                                <span className="text-gray-500">Other:</span>
                                <span className="font-medium">₹{value.other.toFixed(2)}</span>
                            </div>
                        )}
                        {row.totalPaid && (
                            <div className="flex justify-between border-t pt-0.5 mt-1">
                                <span className="text-gray-700 font-medium">Total:</span>
                                <span className="font-bold text-green-600">₹{row.totalPaid}</span>
                            </div>
                        )}
                    </div>
                );
            }
        }
    ];

    // Export functionality
    const handleExportData = async (format) => {
        if (!activeDateRange) {
            toast.error('Please select a date range first');
            return;
        }

        try {
            toast.loading('Preparing export...');

            const startTimestamp = new Date(activeDateRange.startDate).setHours(0, 0, 0, 0);
            const endTimestamp = new Date(activeDateRange.endDate).setHours(23, 59, 59, 999);

            const searchObj = {
                createdAt: {
                    $gte: startTimestamp,
                    $lte: endTimestamp
                },
                status: { $nin: ['cancelled'] },
                billStatus: { $nin: ['CANCELLED'] },
                'payment.paymentStatus': 'Paid'
            };

            // Fetch all data
            const json = {
                page: 1,
                limit: 10000,
                search: searchObj
            };

            const res = await HitApi(json, searchBill);

            if (res?.statusCode === 200 && res?.data?.docs) {
                const allData = res.data.docs;

                if (allData.length === 0) {
                    toast.dismiss();
                    toast.error('No data to export');
                    return;
                }

                // Process for export
                const exportData = processBillsDataForExport(allData);

                const exportHeaders = [
                    { title: 'Customer Name', key: 'customerName' },
                    { title: 'Invoice Date', key: 'invoiceDate' },
                    { title: 'Invoice No.', key: 'invoiceNo' },
                    { title: 'Invoice Type', key: 'invoiceType' },
                    { title: 'Remarks', key: 'remarks' },
                    { title: 'Taxable Value', key: 'taxableValue' },
                    { title: 'GST', key: 'gst' },
                    { title: 'Invoice Amount', key: 'invoiceAmount' },
                    { title: 'Cash', key: 'cash' },
                    { title: 'UPI', key: 'upi' },
                    { title: 'Wallet', key: 'wallet' },
                    { title: 'Card', key: 'card' },
                    { title: 'Total Paid', key: 'totalPaid' }
                ];

                const filename = `bill_report_${activeDateRange.startDate}_to_${activeDateRange.endDate}`;

                toast.dismiss();

                if (format === 'csv') {
                    exportToCSV(exportData, exportHeaders, `${filename}.csv`);
                    toast.success(`Exported ${exportData.length} rows to CSV`);
                }
            } else {
                toast.dismiss();
                toast.error('Failed to fetch data for export');
            }
        } catch (error) {
            toast.dismiss();
            toast.error('Error exporting data');
            console.error('Export error:', error);
        }
    };

    // Process bills for export (same as processBillsData but without React rendering)
    const processBillsDataForExport = (bills) => {
        const rows = [];

        // Filter out pending and cancelled bills
        const excludedStatuses = ['pending', 'cancelled'];
        const filteredBills = bills.filter((bill) =>
            !excludedStatuses.includes(bill.status?.toLowerCase()) &&
            !excludedStatuses.includes(bill.billStatus?.toLowerCase())
        );

        filteredBills.forEach((bill) => {
            const customerName = bill.client?.name || 'N/A';
            const invoiceDate = formatDate(bill.timestamp);
            const invoiceNo = bill.billNumber || 'N/A';
            const changeReturned = bill.changeReturned || 0;
            const paymentBreakdown = getPaymentBreakdown(bill.payment, changeReturned);
            const totalPaid = (bill.payment?.totalPaid || 0) - changeReturned;
            const remarks = bill.remarks || '';
            const roundOffAmount = bill.calculations?.totals?.roundOffAmount || 0;

            let isFirstRow = true;
            let roundOffApplied = false;

            // Process Services
            if (bill.services && bill.services.length > 0) {
                let servicesTaxableValue = 0;

                bill.services.forEach((service) => {
                    const qty = service.quantity || 1;
                    const price = (service.pricing?.finalPrice || service.pricing?.totalPrice || 0) * qty;
                    servicesTaxableValue += price;
                });

                // Add roundoff to first category
                if (!roundOffApplied && roundOffAmount !== 0) {
                    servicesTaxableValue += roundOffAmount;
                    roundOffApplied = true;
                }

                const taxableValue = servicesTaxableValue / (1 + SERVICE_GST_RATE / 100);
                const gstAmount = servicesTaxableValue - taxableValue;

                rows.push({
                    customerName,
                    invoiceDate,
                    invoiceNo,
                    invoiceType: 'Services',
                    remarks,
                    taxableValue: taxableValue.toFixed(2),
                    gst: gstAmount.toFixed(2),
                    invoiceAmount: servicesTaxableValue.toFixed(2),
                    cash: paymentBreakdown.cash > 0 ? paymentBreakdown.cash.toFixed(2) : '',
                    upi: paymentBreakdown.upi > 0 ? paymentBreakdown.upi.toFixed(2) : '',
                    wallet: paymentBreakdown.wallet > 0 ? paymentBreakdown.wallet.toFixed(2) : '',
                    card: paymentBreakdown.card > 0 ? paymentBreakdown.card.toFixed(2) : '',
                    totalPaid: totalPaid.toFixed(2),
                    _billId: bill._id,
                    _isFirst: true
                });
                isFirstRow = false;
            }

            // Process Products
            if (bill.products && bill.products.length > 0) {
                let productsTaxableValue = 0;

                bill.products.forEach((product) => {
                    const qty = product.quantity || 1;
                    const price = (product.pricing?.finalPrice || product.pricing?.totalPrice || 0) * qty;
                    productsTaxableValue += price;
                });

                // Add roundoff to products if not already applied to services
                if (!roundOffApplied && roundOffAmount !== 0) {
                    productsTaxableValue += roundOffAmount;
                    roundOffApplied = true;
                }

                const taxableValue = productsTaxableValue / (1 + PRODUCT_GST_RATE / 100);
                const gstAmount = productsTaxableValue - taxableValue;

                rows.push({
                    customerName: isFirstRow ? customerName : '',
                    invoiceDate: isFirstRow ? invoiceDate : '',
                    invoiceNo,
                    invoiceType: 'Products',
                    remarks: isFirstRow ? remarks : '',
                    taxableValue: taxableValue.toFixed(2),
                    gst: gstAmount.toFixed(2),
                    invoiceAmount: productsTaxableValue.toFixed(2),
                    cash: isFirstRow && paymentBreakdown.cash > 0 ? paymentBreakdown.cash.toFixed(2) : '',
                    upi: isFirstRow && paymentBreakdown.upi > 0 ? paymentBreakdown.upi.toFixed(2) : '',
                    wallet: isFirstRow && paymentBreakdown.wallet > 0 ? paymentBreakdown.wallet.toFixed(2) : '',
                    card: isFirstRow && paymentBreakdown.card > 0 ? paymentBreakdown.card.toFixed(2) : '',
                    totalPaid: isFirstRow ? totalPaid.toFixed(2) : '',
                    _billId: bill._id,
                    _isFirst: isFirstRow
                });
            }

            // Process Memberships
            if (bill.newMemberships && bill.newMemberships.length > 0) {
                let membershipsTaxableValue = 0;

                bill.newMemberships.forEach((membership) => {
                    const price = membership.pricing?.finalPrice || membership.pricing?.purchaseAmount || 0;
                    membershipsTaxableValue += price;
                });

                // Add roundoff to memberships if not already applied
                if (!roundOffApplied && roundOffAmount !== 0) {
                    membershipsTaxableValue += roundOffAmount;
                    roundOffApplied = true;
                }

                const isFirst = isFirstRow;

                rows.push({
                    customerName: isFirst ? customerName : '',
                    invoiceDate: isFirst ? invoiceDate : '',
                    invoiceNo,
                    invoiceType: 'Memberships',
                    remarks: isFirst ? remarks : '',
                    taxableValue: '0.00',
                    gst: '0.00',
                    invoiceAmount: membershipsTaxableValue.toFixed(2),
                    cash: isFirst && paymentBreakdown.cash > 0 ? paymentBreakdown.cash.toFixed(2) : '',
                    upi: isFirst && paymentBreakdown.upi > 0 ? paymentBreakdown.upi.toFixed(2) : '',
                    wallet: isFirst && paymentBreakdown.wallet > 0 ? paymentBreakdown.wallet.toFixed(2) : '',
                    card: isFirst && paymentBreakdown.card > 0 ? paymentBreakdown.card.toFixed(2) : '',
                    totalPaid: isFirst ? totalPaid.toFixed(2) : '',
                    _billId: bill._id,
                    _isFirst: isFirst
                });
            }
        });

        // Clean up internal fields before export
        return rows.map(({ _billId, _isFirst, ...rest }) => rest);
    };

    return (
        <div className="p-5">
            <PageHeader
                title={'Bill Reports'}
                description={'Export bills with separate Services, Products and Memberships breakdown'}
            />

            <div className="mb-4 p-4 bg-white rounded-lg shadow border">
                <div className="flex flex-wrap items-center gap-4">
                    <DateSearch
                        onSearch={handleDateRangeSearch}
                        onClear={handleClearSearch}
                    />

                    {activeDateRange && processedData.length > 0 && (
                        <button
                            onClick={() => handleExportData('csv')}
                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center gap-2"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Export CSV
                        </button>
                    )}
                </div>

                {/* GST Info */}
                <div className="mt-3 flex gap-4 text-xs text-gray-600">
                    <span className="flex items-center gap-1">
                        <span className="w-3 h-3 bg-blue-100 rounded"></span>
                        Services: 18% GST
                    </span>
                    <span className="flex items-center gap-1">
                        <span className="w-3 h-3 bg-green-100 rounded"></span>
                        Products: 18% GST
                    </span>
                    <span className="flex items-center gap-1">
                        <span className="w-3 h-3 bg-purple-100 rounded"></span>
                        Memberships: No GST
                    </span>
                </div>
            </div>

            {!activeDateRange ? (
                <div className="bg-white rounded-lg shadow border p-8 text-center">
                    <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Select Date Range</h3>
                    <p className="text-gray-500">Please select a date range to view and export bill reports</p>
                </div>
            ) : (
                <AppTable
                    TH={tableHeaders}
                    TD={processedData}
                    isLoading={isSearching}
                    emptyMessage="No bills found for selected date range"
                />
            )}
        </div>
    );
}

export default BillReportMaster;
